import { Field, IconButton, Modal, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import type { DatasetFile } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import styles from "./ExtraFileRenameDialog.module.css";

export type ExtraFileRenameDialogProps = {
  file: DatasetFile;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  // 새 relative_path 를 전달한다. 부모 폴더는 유지하고 파일명만 바꾼 값이다.
  onSubmit: (relativePath: string) => void;
};

// extra 파일 rename. 파일명만 편집한다. 부모 폴더는 read-only 로 표시해 의도하지 않은 이동을 막는다.
// 제출하면 부모 폴더와 새 파일명으로 relative_path 를 다시 만들어 전달한다.
// 제출 상태는 부모 컴포넌트가 prop 으로 준다.
export function ExtraFileRenameDialog({
  file,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: ExtraFileRenameDialogProps): ReactNode {
  const initialName = file.filename;
  const [name, setName] = useState(initialName);
  // 입력한 이름은 유니코드 NFC 로 바꾼다. 붙여 넣은 macOS 파일 이름은 NFD 일 수 있다. 서버는 본문에
  // NFC 가 아닌 문자열이 있으면 요청을 422 로 거부한다. 부모 폴더 경로는 서버가 준 값이라 바꾸지 않는다.
  const trimmed = name.trim().normalize("NFC");
  const unchanged = trimmed === initialName;

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (submitting || trimmed === "" || unchanged) {
      return;
    }
    const relativePath = file.parent_path ? `${file.parent_path}/${trimmed}` : trimmed;
    onSubmit(relativePath);
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      aria-labelledby="extra-file-rename-title"
    >
      <HatModalHeader
        title="파일 이름을 변경합니다."
        titleId="extra-file-rename-title"
        close={{ disabled: submitting }}
      />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Field label="폴더" layout="stack">
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.folder}>
            {file.parent_path === "" ? "(루트)" : file.parent_path}
          </Text>
        </Field>
        <Field label="이름" layout="stack" disabled={submitting}>
          <TextInput
            value={name}
            maxLength={255}
            required
            onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          />
        </Field>
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={submitting}
            onClick={onClose}
          />
          <IconButton
            text={submitting ? "저장 중" : "저장"}
            type="submit"
            variant="solid"
            disabled={submitting || trimmed === "" || unchanged}
          />
        </div>
      </form>
    </Modal>
  );
}
